/**
 * Formatting Utilities
 * Handles value formatting, string escaping, and HTML processing
 */

import { mapReferencesForExport, parseVariableReferences, type ExportPlatform } from '@/lib/utils/variableReferences';

/**
 * Escape special characters for Lua strings
 */
export function escapeLuaString(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t');
}

/**
 * Strip HTML tags from a string, returning plain text
 */
export function stripHtmlTags(html: string): string {
  if (!html || typeof html !== 'string') return html;
  
  // Replace common block elements with newlines for readability
  let text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n');
  
  // Remove all HTML tags
  text = text.replace(/<[^>]*>/g, '');
  
  // Decode common HTML entities
  text = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'");
  
  // Clean up multiple consecutive newlines and trim
  text = text.replace(/\n{3,}/g, '\n\n').trim();
  
  return text;
}

/**
 * Extract text value from TranslatableValue objects
 * Helper function to handle the new translatable value format
 */
export function extractTextValue(value: any): any {
  if (value && typeof value === 'object' && 'text' in value) {
    // TranslatableValue format - extract the text
    return value.text;
  }
  return value;
}

/**
 * Extract string_ids from TranslatableValue objects
 * Returns array of string_ids for translatable fields
 */
export function extractStringIds(value: any): string[] {
  const stringIds: string[] = [];
  
  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (item && typeof item === 'object' && 'string_id' in item && item.string_id) {
        stringIds.push(item.string_id);
      }
    });
  } else if (value && typeof value === 'object' && 'string_id' in value && value.string_id) {
    stringIds.push(value.string_id);
  }
  
  return stringIds;
}

/**
 * Process variable references in an exported string value.
 * Converts `{{ key.name }}` tokens to the appropriate platform syntax.
 *
 * @param text - The string value that may contain variable references
 * @param platform - Target export platform ('unity' | 'roblox' | 'json' | 'godot' | 'generic')
 * @param namingFn - Optional naming convention function
 * @returns The processed string with platform-specific variable interpolation
 */
export function processVariableReferencesForExport(
  text: string,
  platform: ExportPlatform,
  namingFn?: (name: string) => string
): string {
  if (!text || typeof text !== 'string') return text;
  // Only process if the text actually contains variable references
  if (parseVariableReferences(text).length === 0) return text;
  return mapReferencesForExport(text, platform, namingFn);
}

/**
 * Format a value for code output
 */
export function formatValue(value: any, mappedType: string, platform: string): string {
  // Extract text from TranslatableValue objects first
  const extractedValue = extractTextValue(value);
  
  if (extractedValue === null || extractedValue === undefined || extractedValue === '') {
    // Return appropriate empty/default value based on type
    if (mappedType.includes('string')) return '""';
    if (mappedType.includes('number') || mappedType.includes('int')) return '0';
    if (mappedType.includes('{') || mappedType.includes('[]') || mappedType.includes('Array')) return '{}';
    return '""';
  }
  
  // Handle arrays
  if (Array.isArray(extractedValue)) {
    // For arrays, extract text from each item (in case of TranslatableValue arrays)
    const extractedItems = extractedValue.map(extractTextValue);
    
    if (platform === 'roblox') {
      const items = extractedItems.map(v => formatValue(v, 'string', platform)).join(', ');
      return `{${items}}`;
    } else if (platform === 'cpp') {
      const items = extractedItems.map(v => formatValue(v, 'string', platform)).join(', ');
      return `{${items}}`;
    }
  }
  
  // Handle strings
  if (typeof extractedValue === 'string') {
    if (platform === 'roblox') {
      return `"${escapeLuaString(extractedValue)}"`;
    } else if (platform === 'cpp') {
      return `"${extractedValue.replace(/"/g, '\\"')}"`;
    }
    return `"${extractedValue}"`;
  }
  
  // Handle numbers
  if (typeof extractedValue === 'number') {
    return extractedValue.toString();
  }
  
  // Handle booleans
  if (typeof extractedValue === 'boolean') {
    return extractedValue.toString();
  }
  
  return String(extractedValue);
}
